import { useCallback, useEffect, useState } from "react";
import { viewsApi } from "@/api/endpoints";
import type { SavedView } from "@/api/types";

type Props = {
  entity: string;
  filters: Record<string, string>;
  sort: string | null;
  onApply: (filters: Record<string, string>, sort: string | null) => void;
};

export function SavedViewsBar({ entity, filters, sort, onApply }: Props) {
  const [views, setViews] = useState<SavedView[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    viewsApi
      .list(entity)
      .then((d) => setViews(d.items))
      .catch(() => setViews([]));
  }, [entity]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  function save() {
    const name = window.prompt("Name this view");
    if (!name || !name.trim()) return;
    setError(null);
    viewsApi
      .create({ entity, name: name.trim(), filters, sort })
      .then(() => refresh())
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to save view"));
  }

  function remove(id: string) {
    viewsApi
      .remove(id)
      .then(() => refresh())
      .catch(() => undefined);
  }

  return (
    <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
      <span className="field-label">Views:</span>
      {views.length === 0 ? <span className="field-hint">none saved</span> : null}
      {views.map((view) => (
        <span
          key={view.id}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.3rem",
            padding: "0.2rem 0.5rem",
            borderRadius: 999,
            border: "1px solid var(--border)",
            fontSize: "0.8rem",
          }}
        >
          <button
            type="button"
            onClick={() => onApply(view.filters ?? {}, view.sort)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0 }}
          >
            {view.name}
          </button>
          <button
            type="button"
            onClick={() => remove(view.id)}
            aria-label={`Delete ${view.name}`}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 0 }}
          >
            ×
          </button>
        </span>
      ))}
      <button type="button" className="btn btn-secondary" onClick={save}>
        ＋ Save view
      </button>
      {error ? <span style={{ color: "#dc2626", fontSize: "0.8rem" }}>{error}</span> : null}
    </div>
  );
}
